import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import QRCodeStyling, {
  Options as QRCodeStylingOptions,
} from "qr-code-styling";
import { useTranslation } from "react-i18next";
import IconShare from "@/assets/icons/share.svg?react";
import IconAlertCircle from "@/assets/icons/alert-circle.svg?react";
import BaseButton from "../../components/base/button/BaseButton.tsx";
import ParticipantCard from "../challenges/components/ParticipantCard.tsx";
import NoData from "../../components/common/NoData.tsx";
import IconDonFilled from "@/assets/icons/don-filled.svg?react";
import StoriesModal from "../../components/stories/StoriesModal.tsx";
import PageWrapper from "../../components/PageWrapper.tsx";
import { useReferralCode, useSomeReferredUsers } from "@/pages/referral/api";
import { useGetProfile } from "@/pages/profile/api";
import { shareURL } from "@telegram-apps/sdk-react";
import { useStoryDetail, useFetchSettings } from "@/pages/home/api";
import UserCardLoading from "@/pages/challenges/components/UserCardLoading.tsx";
import ReferralChart, { ReferralChartLoading } from "./ReferralChart.tsx";
import { isNullOrUndefined } from "@/shared/utils/common.ts";
import { appConfig } from "@/lib/site-config.ts";

const qrOptions: QRCodeStylingOptions = {
  width: 212,
  height: 212,
  margin: 4,
  type: "svg",
  image: "images/don-logo.svg",
  qrOptions: { typeNumber: 0, mode: "Byte", errorCorrectionLevel: "Q" },
  imageOptions: { imageSize: 0.5, margin: 4, crossOrigin: "anonymous" },
  dotsOptions: {
    type: "extra-rounded",
    color: "#000000",
    roundSize: true,
  },
  backgroundOptions: { round: 0, color: "#ffffff" },
  cornersSquareOptions: { type: "extra-rounded", color: "#000000" },
};

const useQRCodeStyling = (
  options: QRCodeStylingOptions
): QRCodeStyling | null => {
  const qrCodeStyling: QRCodeStyling = new QRCodeStyling(options);
  return qrCodeStyling;
};

const ReferralPage = () => {
  const referralCodeMutation = useReferralCode();
  const { data: story, isLoading: isStoryLoading } = useStoryDetail(
    appConfig.storySlug.referralRules
  );
  const qrCode = useQRCodeStyling(qrOptions);
  const qrCodeInstance = useMemo(() => qrCode, []);

  const { data: profile, isLoading: isProfileLoading } = useGetProfile();

  // Reward tiers come from the backend AppSettings (single source of truth),
  // not a hardcoded config. Index 0: 0-100, 1: 100-1000, 2: 1000+.
  const { data: settings, isLoading: isSettingsLoading } = useFetchSettings();
  const tierAmounts = useMemo(
    () => [
      Number(settings?.referral_1_100 ?? 0),
      Number(settings?.referral_101_1000 ?? 0),
      Number(settings?.referral_1000 ?? 0),
    ],
    [settings]
  );

  const [referralCode, setReferralCode] = useState("");
  useEffect(() => {
    if (profile && profile.referral_code) {
      setReferralCode(profile.referral_code);
    } else {
      referralCodeMutation.mutate(undefined, {
        onSuccess: (res) => {
          setReferralCode(res.referral_code);
        },
      });
    }
  }, [profile, referralCodeMutation]);

  const miniAppUrl = `${
    import.meta.env.VITE_APP_TG_URL
  }?startapp=ref_${referralCode}`;

  const { t } = useTranslation();

  const qrRef = useRef<any>(null);

  useEffect(() => {
    qrCodeInstance?.append(qrRef.current);
  }, [qrRef, qrCodeInstance]);

  useEffect(() => {
    qrCodeInstance?.update({
      data: `${import.meta.env.VITE_APP_TG_URL}?startapp=ref_${referralCode}`,
    });
  }, [referralCode, qrCodeInstance]);

  const storiesModal = useRef(null);

  const showStories = () => {
    if (storiesModal.current) {
      // @ts-expect-error i know what i'm doing
      storiesModal.current.openStories(0);
    }
  };

  const shareUrl = () => {
    if (shareURL.isAvailable()) {
      shareURL(miniAppUrl);
    }
  };

  const getAwardAmount = (count: number) => {
    if (count >= 0 && count < 100) {
      return tierAmounts[0];
    }

    if (count >= 100 && count < 1000) {
      return tierAmounts[1];
    }

    return tierAmounts[2];
  };

  const { data: referredUsers, isLoading } = useSomeReferredUsers();

  const getDonAwardAmount = (count: number) => {
    if (profile?.is_premium) {
      return getAwardAmount(count) * 2;
    }
    return getAwardAmount(count);
  };

  return (
    <PageWrapper backPath="/">
      <div className="container py-3">
        {!isProfileLoading && !referralCodeMutation.isPending && (
          <div
            ref={qrRef}
            className="rounded-2xl overflow-hidden mx-auto w-[212px] h-[212px]"
          ></div>
        )}
        <h1 className="mt-4 mb-1 text-center text-xl leading-130 font-semibold text-white">
          {t("invite_your_friends")}
        </h1>
        <p className="text-center text-sm font-normal text-gray-1 leading-130">
          {t("invite_your_friends_text", {
            amount: getDonAwardAmount(
              referredUsers?.count ? referredUsers.count : 0
            ),
          })}
        </p>
        <div>
          {isLoading || isSettingsLoading ? (
            <ReferralChartLoading />
          ) : (
            !isNullOrUndefined(referredUsers?.count) && (
              <ReferralChart
                invitedUsersCount={referredUsers!.count}
                isPremium={profile?.is_premium}
                tierAmounts={tierAmounts}
              />
            )
          )}
        </div>
        <div className="flex justify-between items-center gap-2 w-full my-5">
          {referralCodeMutation.isPending ? (
            <Fragment>
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="skeleton w-full h-11 rounded-xl" />
              ))}
            </Fragment>
          ) : (
            <Fragment>
              <BaseButton
                vibrate={true}
                className="w-full !gap-1.5 col-span-2"
                variant="secondary"
                text={t("rules")}
                prefix={
                  <IconAlertCircle strokeWidth="2" width="20" height="20" />
                }
                onClick={showStories}
              />
              <BaseButton
                text={t("share")}
                className="flex-center w-full gap-2 bg-blue rounded-xl text-sm font-semibold"
                onClick={shareUrl}
                suffix={
                  <IconShare className="text-white size-5" strokeWidth="1.5" />
                }
              />
            </Fragment>
          )}
        </div>
        <div className="flex-center-between mt-5 mb-2">
          <h2 className="text-lg text-white font-bold leading-130">
            {t("invited_members")}
          </h2>
          {/* <Link
          to={`/referral/invited`}
            className="text-gray-1 text-xs font-medium leading-130 flex-y-center gap-0.5"
          >
            {t("all_button")}
            <span width="18" height="18" strokeWidth="1.6" />
          </Link> */}
        </div>
        {/*Todo: add more state*/}
        <div className="flex flex-col gap-3">
          {isLoading ? (
            <>
              {Array.from({ length: 5 }).map((_, index) => (
                <UserCardLoading key={index} />
              ))}
            </>
          ) : referredUsers?.count ? (
            referredUsers?.results.map((item, index) => (
              <ParticipantCard
                key={index}
                avatar={item.avatar}
                username={item.username}
                award={item.award}
                isPremium={item.is_premium}
                fullName={item.full_name}
                referredAt={item.referred_at}
                isReferral
              />
            ))
          ) : (
            <NoData
              className="py-10"
              image={<IconDonFilled width="48" height="48" />}
              title={t("no_invited_users")}
              description={t("no_invited_users_description")}
            />
          )}
        </div>

        {!isStoryLoading && story && (
          <StoriesModal ref={storiesModal} storiesData={[story]} />
        )}
      </div>
    </PageWrapper>
  );
};

export default ReferralPage;
